import { useCallback, useEffect, useRef, useState } from 'react';
import { Autocomplete, Popper, TextField } from '@mui/material';
import debounce from 'lodash.debounce';
import * as R from 'ramda';

import { Icon } from '@atom/mui';
import { GoogleMapsLocation } from '@atom/types/gmapsLocation';
import { parseCoordinates } from '@atom/utilities/mapUtilities';

const DEBOUNCE_TIME = 300;

const mintSessionToken = () =>
  new window.google.maps.places.AutocompleteSessionToken();

const styles = {
  leftIconMarkerStyle: {
    minWidth: '20px',
    maxWidth: '20px',
    paddingRight: '5px',
  },
  menuItem: {
    fontSize: 13,
    overflow: 'hidden',
    paddingRight: '1rem',
    paddingLeft: '1rem',
  },
};

interface Props {
  initialValue?: string;
  hintText?: string;
  textFieldStyle?: object;
  onSelect?: (place: GoogleMapsLocation, searchTerm?: string) => void;
  anchor?: any;
  popoverWidth?: string | number;
}

const GooglePlacesAutoComplete = ({
  initialValue,
  hintText,
  textFieldStyle,
  onSelect,
  anchor,
  popoverWidth,
}: Props) => {
  const [searchText, setSearchText] = useState<string>(initialValue || '');
  const [suggestions, setSuggestions] = useState<
    google.maps.places.AutocompleteSuggestion[]
  >([]);
  const sessionTokenRef =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null);

  if (sessionTokenRef.current === null) {
    sessionTokenRef.current = mintSessionToken();
  }

  useEffect(() => {
    setSearchText(initialValue || '');
  }, [initialValue]);

  const fetchSuggestions = async (input: string) => {
    try {
      const { suggestions: nextSuggestions } =
        await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
          {
            input,
            sessionToken: sessionTokenRef.current,
          },
        );

      setSuggestions(nextSuggestions);
    } catch {
      setSuggestions([]);
    }
  };

  const fetchSuggestionsDebounced = useCallback<(input: string) => void>(
    debounce(fetchSuggestions, DEBOUNCE_TIME),
    [],
  );

  useEffect(() => {
    if (R.isEmpty(searchText)) {
      setSuggestions([]);
      return;
    }

    fetchSuggestionsDebounced(searchText);
  }, [searchText]);

  const onNewRequest = async (_, newValue) => {
    const coordinates = parseCoordinates(searchText);
    if (coordinates) {
      onSelect(coordinates);
      return;
    }

    if (!newValue?.placePrediction) {
      return;
    }

    const place = newValue.placePrediction.toPlace();
    try {
      await place.fetchFields({ fields: ['location', 'formattedAddress'] });
    } catch {
      return;
    }

    if (place.location) {
      onSelect(
        {
          lat: place.location.lat(),
          lng: place.location.lng(),
        },
        place.formattedAddress,
      );

      setSearchText(place.formattedAddress || '');
    }

    sessionTokenRef.current = mintSessionToken();
  };

  const popoverProps = {
    style: {
      ...(popoverWidth && {
        width: popoverWidth,
      }),
    },
    ...(anchor && {
      anchorEl: anchor,
    }),
  };

  const getMenuItems = (
    props,
    option: google.maps.places.AutocompleteSuggestion,
  ) => {
    const { key, ...optionProps } = props;

    return (
      <li key={key} style={styles.menuItem} {...optionProps}>
        <Icon style={styles.leftIconMarkerStyle}>location_on</Icon>
        <span>{option?.placePrediction?.text?.text}</span>
      </li>
    );
  };

  return (
    <Autocomplete
      options={suggestions}
      freeSolo
      clearIcon={null}
      filterOptions={option => option}
      getOptionLabel={option =>
        typeof option === 'string'
          ? option
          : option.placePrediction?.text?.text || ''
      }
      renderOption={getMenuItems}
      inputValue={searchText}
      onInputChange={(_event, value) => setSearchText(value || '')}
      onChange={onNewRequest}
      slots={{ popper: params => <Popper {...params} {...popoverProps} /> }}
      renderInput={params => (
        <TextField
          variant="standard"
          {...params}
          placeholder={hintText || ''}
          fullWidth
          style={{ paddingTop: '0.25rem', ...textFieldStyle }}
          slotProps={{
            input: { disableUnderline: true, ...params.InputProps },
          }}
        />
      )}
      style={{ width: '100%' }}
    />
  );
};

export default GooglePlacesAutoComplete;
